import * as React from "react";
import { observer } from "mobx-react";
import { UitkCard, UitkCardMedia, UitkCardLink, UitkCardTextSegment } from "uitk-react-cards";
import { UitkFigure, UitkFigureAspectRatioType } from "uitk-react-images";
import { Action } from "components/utility/analytics/FlexAnalyticsUtils";
import { TrackedLink } from "components/utility/analytics/TrackedLink";
import { UitkBadge, UitkVipBadge } from "uitk-react-badge";
import { CampaignRecommendationsWithOffersQuery } from "../../../../../../__generated__/api";
import Offers = CampaignRecommendationsWithOffersQuery.Offers;
import { UitkPriceLockup } from "uitk-react-price-lockup";
import { withStores } from "stores";
import { RouteStore } from "bernie-core";
import { Link } from "react-router-dom";
import { MabTrackEvent, withMabTracker } from "src/components/utility/analytics/MabTracker";
import { UitkLayoutGrid } from "uitk-react-layout-grid";
import { LazyLoadedImage } from "components/utility/LazyLoadedImage/LazyLoadedImage";

export interface TallHotelInventoryCardProps {
  offer: Offers;
  cardIndex: number;
  campaignId: string;
  openDialog: (dialogPath: string) => void;
  closeDialog: () => void;
  moduleName: string;
  withBorder?: boolean;
  routeStore: RouteStore;
  priceDisclaimerLabel: string;
  strikeThroughPrice: string;
  disclaimerInfoDialog: () => JSX.Element;
  currentPrice: string;
  a11yPrice: string;
  cardClicked: (x: MabTrackEvent) => null;
}

export const TallHotelInventoryCard = withMabTracker(
  withStores("routeStore")(
    observer(
      ({
        offer,
        cardIndex,
        campaignId,
        moduleName,
        withBorder,
        routeStore,
        priceDisclaimerLabel,
        strikeThroughPrice,
        disclaimerInfoDialog,
        currentPrice,
        a11yPrice,
        cardClicked,
      }: TallHotelInventoryCardProps) => {
        if (offer.__typename !== "HotelOfferItem") {
          return null;
        }
        const discountBadge = offer.discountBadge?.text;
        const isFreeCancellation = offer.freeCancellationMessage;

        return (
          <UitkCard
            className="TallHotelInventoryCard uitk-flex-justify-content-flex-start uitk-flex-column"
            withBorder={withBorder}
          >
            <UitkCardMedia>
              <UitkFigure ratio={UitkFigureAspectRatioType.R21_9}>
                {offer.vipMessage && (
                  <UitkVipBadge className="all-t-margin-three all-l-margin-three hotelBadge" text={offer.vipMessage} />
                )}
                <LazyLoadedImage src={offer.image.url} alt={offer.image.description} />
              </UitkFigure>
            </UitkCardMedia>
            <div className="uitk-card-content uitk-cell">
              <UitkCardTextSegment>
                <div>
                  <h3 className="truncate all-b-padding-half pwa-theme--grey-900 uitk-type-heading-500">
                    {offer.name}
                  </h3>
                  <span className="uitk-type-300">{offer.location}</span>

                  {offer.guestReviewRating && (
                    <div className="uitk-cell all-r-padding-one reviewRating">
                      <span className="uitk-type-heading-300 uitk-cell all-r-padding-one">
                        {offer.guestReviewRating}
                      </span>
                      <span className="uitk-type-300 pwa-theme--grey-700">{offer.guestReviewCount}</span>
                    </div>
                  )}
                </div>
              </UitkCardTextSegment>
              <UitkCardTextSegment>
                <div>
                  <hr className={discountBadge ? "separatorLine separatorLineWidth" : "separatorLine"} />
                  {offer.discountBadge?.text && (
                    <div className="uitk-cell uitk-type-right badgeStyle">
                      <UitkBadge text={offer.discountBadge?.text} />
                    </div>
                  )}
                </div>
                <UitkLayoutGrid columns={2} space="two" className={"all-t-padding-four"}>
                  <>
                    <div>
                      {offer.freeCancellationMessage && (
                        <div className="uitk-cell uitk-type-300 uitk-type-bold all-b-padding-two freeCancellation all-t-padding-one">
                          {offer?.freeCancellationMessage}
                        </div>
                      )}
                      <div className={isFreeCancellation ? "uitk-type-200" : "uitk-type-200 dateStyle"}>
                        {offer?.dates}
                      </div>
                    </div>
                    <div className="uitk-cell uitk-type-right">
                      <UitkPriceLockup
                        lockupPrice={currentPrice}
                        priceSubtextFirst={offer.lodgingOffer?.price?.qualifierText}
                        a11yPrice={a11yPrice}
                        a11yStrikeThroughDialogTrigger={priceDisclaimerLabel}
                        strikeThroughPrice={strikeThroughPrice}
                        strikeThroughTrigger={
                          <Link
                            to={{
                              pathname: routeStore.pathname,
                              search: routeStore.mergeQueryString(routeStore.search, {
                                pwaDialog: `disclaimer-info-${campaignId}-${cardIndex}`,
                              }),
                            }}
                            className="disclaimerInfoIcon"
                          />
                        }
                      />
                      {disclaimerInfoDialog()}
                    </div>
                  </>
                </UitkLayoutGrid>
              </UitkCardTextSegment>
              <UitkCardLink>
                {offer.lodgingOffer && (
                  <TrackedLink
                    moduleName={moduleName}
                    action={Action.CLICK}
                    href={offer.lodgingOffer?.actionLink.href}
                    rfrr={`card-${campaignId}-${cardIndex}`}
                    onClick={cardClicked}
                  >
                    {offer.location}
                  </TrackedLink>
                )}
              </UitkCardLink>
            </div>
          </UitkCard>
        );
      }
    )
  )
);
